import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type FormEvent,
  type SVGProps,
} from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import {
  getSession,
  isRememberedSession,
  logout,
  replaceSession,
} from "../../lib/auth";
import { confirmUpdate } from "../../lib/confirm-service";
import { formatDateTime } from "../../lib/datetime";
import { useHospitalBrand } from "../../lib/hospital-brand";
import { parseNoticeContent, type NoticeImage } from "../../lib/notice-content";
import {
  canSearchPatientGlobal,
  isPatientBillSearch,
  normalizePatientGlobalSearch,
  usePatientGlobalSearch,
} from "../../lib/patient-uhid-search";
import { useTheme } from "../../lib/theme";
import { useToast } from "../../lib/toast";
import { changeOwnPassword, endSession } from "../../pages/auth/auth.api";
import {
  usePatientGlobalLookup,
  usePatientsSummary,
} from "../../pages/patients/patient.queries";
import type { PatientUhidOption } from "../../pages/patients/types";
import { useSettingsList } from "../../pages/settings/settings.queries";
import type { SettingsRecord } from "../../pages/settings/types";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { Modal } from "../ui/Modal";
import {
  IconBaby,
  IconBed,
  IconBell,
  IconBillCheck,
  IconBloodBank,
  IconChevron,
  IconClose,
  IconCross,
  IconDashboard,
  IconDialysis,
  IconEmergency,
  IconFlask,
  IconHospital,
  IconIdCard,
  IconLock,
  IconLogout,
  IconMenu,
  IconMoon,
  IconOtLamp,
  IconPill,
  IconReports,
  IconSearch,
  IconSettings,
  IconStethoscope,
  IconStore,
  IconSun,
  IconUsers,
  IconVaccination,
} from "./icons";

type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

interface NavChild {
  label: string;
  to?: string;
  end?: boolean;
  children?: NavChild[];
}

interface NavItem {
  label: string;
  icon: NavIcon;
  to?: string;
  end?: boolean;
  countKey?: "patients";
  children?: NavChild[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
  hideHeader?: boolean;
}

type NoticePriority = "high" | "medium" | "low";

interface NoticeItem {
  id: string;
  notice: string;
  images: NoticeImage[];
  priorityLevel: NoticePriority;
  createdAt: string | null;
  updatedAt: string | null;
}

const noticeFromRecord = (record: SettingsRecord): NoticeItem | null => {
  if (typeof record.notice !== "string" || !record.notice.trim()) return null;
  const content = parseNoticeContent(record.notice);
  if (!content.text && content.images.length === 0) return null;
  const priorityLevel =
    record.priorityLevel === "high" ||
    record.priorityLevel === "medium" ||
    record.priorityLevel === "low"
      ? record.priorityLevel
      : "low";
  return {
    id: record.id,
    notice: content.text,
    images: content.images,
    priorityLevel,
    createdAt: typeof record.createdAt === "string" ? record.createdAt : null,
    updatedAt: typeof record.updatedAt === "string" ? record.updatedAt : null,
  };
};

const navGroups: NavGroup[] = [
  {
    label: "Main menu",
    hideHeader: true,
    items: [
      { label: "Dashboard", icon: IconDashboard, to: "/", end: true },
      {
        label: "Patients",
        icon: IconUsers,
        to: "/patients",
        countKey: "patients",
      },
      { label: "Nursery", icon: IconBaby, to: "/nursery" },
      {
        label: "OPD",
        icon: IconStethoscope,
        children: [
          { label: "OPD", to: "/opd", end: true },
          { label: "Appointment", to: "/opd/appointments" },
          { label: "Time Schedule Setup", to: "/opd/time-schedule" },
          { label: "Doctor's Schedule", to: "/opd/doctors-schedule" },
        ],
      },
      { label: "Emergency", icon: IconEmergency, to: "/emg" },
      { label: "IPD / Daycare", icon: IconHospital, to: "/ipd-daycare" },
      { label: "Dialysis", icon: IconDialysis, to: "/dialysis" },
      {
        label: "Investigation",
        icon: IconFlask,
        children: [
          { label: "Investigation Bill", to: "/investigation-bill" },
          { label: "Pathology", to: "/pathology" },
          { label: "Non-Pathology", to: "/non-pathology" },
          { label: "Radiology", to: "/radiology" },
          { label: "Rate Query", to: "/rate-query" },
          { label: "Ready Report", to: "/ready-report" },
        ],
      },
      { label: "OT", icon: IconOtLamp, to: "/ot" },
      { label: "Bed Details", icon: IconBed, to: "/bed-status" },
      { label: "Bill Approval", icon: IconBillCheck, to: "/bill-approval" },
      {
        label: "Pharmacy",
        icon: IconPill,
        children: [
          { label: "Dashboard", to: "/pharmacy", end: true },
          { label: "Medicine Billing", to: "/pharmacy/medicine-billing" },
          { label: "Medicine Requisition", to: "/pharmacy/requisitions" },
          {
            label: "Purchase Order",
            to: "/pharmacy/purchase-orders",
          },
          { label: "Medicine Purchase", to: "/pharmacy/purchases" },
          { label: "Medicine Issue", to: "/pharmacy/issues" },
          {
            label: "Medicine Issue Report",
            to: "/pharmacy/reports/medicine-issue",
          },
          { label: "Medicine List", to: "/pharmacy/medicines" },
          {
            label: "Pharmacy Setup",
            children: [
              { label: "Medicine Category", to: "/pharmacy/categories" },
              { label: "Medicine Unit", to: "/pharmacy/units" },
              { label: "Vendor", to: "/pharmacy/vendors" },
            ],
          },
        ],
      },
      {
        label: "Store",
        icon: IconStore,
        children: [
          { label: "Dashboard", to: "/store", end: true },
          { label: "Requisition", to: "/store/requisitions" },
          { label: "Purchase Order", to: "/store/purchase-orders" },
          { label: "Purchase", to: "/store/purchases" },
          { label: "Item Issue", to: "/store/issues" },
          {
            label: "Work Order",
            children: [
              {
                label: "Master",
                children: [
                  { label: "Type", to: "/store/work-order-types" },
                  { label: "Unit", to: "/store/work-order-units" },
                  { label: "Vendor", to: "/store/work-order-vendors" },
                ],
              },
              { label: "Work Order", to: "/store/work-order" },
              { label: "Generate Bill", to: "/store/work-order-bills" },
            ],
          },
          {
            label: "Return Processing",
            children: [
              { label: "Return to Store", to: "/store/returns/to-store" },
              { label: "Return to Vendor", to: "/store/returns/to-vendor" },
              {
                label: "Repair / Damage Items",
                to: "/store/returns/repair-damage",
              },
            ],
          },
          {
            label: "Items",
            children: [
              { label: "Items", to: "/store/items" },
              { label: "Item Category", to: "/store/item-categories" },
              { label: "Item Unit", to: "/store/item-units" },
              { label: "Item Type", to: "/store/item-types" },
              { label: "Item Company", to: "/store/item-companies" },
              { label: "Item Store", to: "/store/item-stores" },
              { label: "Store Department", to: "/store/departments" },
            ],
          },
          {
            label: "Miscellaneous",
            children: [
              { label: "Bill Manage", to: "/store/miscellaneous-bills" },
              { label: "Expense Ledger Head", to: "/store/expense-ledger-heads" },
              { label: "Expense Ledger Items", to: "/store/expense-ledger-items" },
              { label: "Vendor", to: "/store/miscellaneous-vendors" },
            ],
          },
          { label: "Vendor", to: "/store/vendors" },
          {
            label: "Upcoming Expiry Items",
            to: "/store/reports/upcoming-expiry",
          },
          {
            label: "Reports",
            children: [
              {
                label: "General Stock Reports",
                to: "/store/reports/general-stock",
              },
              {
                label: "Department Stock Reports",
                to: "/store/reports/department-stock",
              },
              { label: "Requisition Report", to: "/store/reports/requisition" },
              {
                label: "Purchase Order Report",
                to: "/store/reports/purchase-order",
              },
              { label: "Purchase Report", to: "/store/reports/purchase" },
              { label: "Item Issue Reports", to: "/store/reports/issue" },
              { label: "Item Stock Report", to: "/store/reports/item-stock" },
            ],
          },
        ],
      },
      {
        label: "Reports",
        icon: IconReports,
        children: [
          {
            label: "Collection",
            children: [
              { label: "Collection", to: "/reports/collection" },
              { label: "My Collection", to: "/reports/my-collection" },
              { label: "Due Reports", to: "/reports/due" },
              // { label: 'Revenue Reports', to: '/reports/revenue' },
            ],
          },
          { label: "Daily Report", to: "/reports/daily" },
          { label: "Hospital Stay", to: "/reports/hospital-stay" },
          { label: "OPD Doctor Payout", to: "/reports/doctor-payout" },
          { label: "Commission Report", to: "/reports/commission" },
          { label: "Test Report", to: "/reports/test" },
          {
            label: "Billing Details",
            children: [
              { label: "All Bill", to: "/reports/bills" },
              { label: "All Receipt", to: "/reports/receipts" },
              { label: "All Refund", to: "/reports/refunds" },
            ],
          },
          {
            label: "Patient Report",
            children: [
              { label: "OPD", to: "/reports/patients/opd" },
              { label: "EMG", to: "/reports/patients/emg" },
              { label: "IPD/DAYCARE", to: "/reports/patients/ipd-daycare" },
              { label: "DIALYSIS", to: "/reports/patients/dialysis" },
              { label: "INVESTIGATION", to: "/reports/patients/investigation" },
            ],
          },
          { label: "Billing Report", to: "/reports/billing-details" },
          { label: "Birth Report", to: "/reports/birth" },
          { label: "Death Report", to: "/reports/death" },
          { label: "Discharge Report", to: "/reports/discharge" },
        ],
      },
      // { label: "MRD", icon: IconMedicalRecords, to: "/mrd" },
      {
        label: "Blood Bank",
        icon: IconBloodBank,
        children: [
          { label: "Dashboard", to: "/blood-bank/dashboard" },
          { label: "Blood Collect", to: "/blood-bank/blood-collection-list" },
          { label: "Blood Testing", to: "/blood-bank/get-blood-testing" },
          { label: "Approval & Stock", to: "/blood-bank/approval-stock" },
          { label: "Blood Issue", to: "/blood-bank/get-blood-issue" },
          { label: "Donors", to: "/blood-bank/doners" },
          { label: "Donation Camps", to: "/blood-bank/donation-camps" },
          { label: "Blood Bank Report", to: "/blood-bank/blood-report" },
        ],
      },
      {
        label: "Vaccination",
        icon: IconVaccination,
        children: [
          { label: "Dashboard", to: "/vaccination/dashboard" },
          { label: "Purchase", to: "/vaccination/listing-purchase" },
          { label: "Vaccine Stocks", to: "/vaccination/stock-details" },
          {
            label: "Vaccination",
            children: [
              {
                label: "Vaccination Lists",
                to: "/vaccination/vaccination-lists",
              },
              {
                label: "New Registration",
                to: "/vaccination/vaccination-register",
              },
            ],
          },
          { label: "Vaccines", to: "/vaccination/vaccines" },
          { label: "AEFI Reports", to: "/vaccination/listing-aefi-reports" },
          { label: "Vendors", to: "/vaccination/vendors" },
        ],
      },
      { label: "Settings", icon: IconSettings, to: "/settings" },
    ],
  },
];

const initialsFor = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const SIDEBAR_RAIL_KEY = "hospital.sidebar-rail";

export const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const session = getSession();
  const hospitalBrand = useHospitalBrand();
  const hospitalTitle = hospitalBrand.title;
  const hospitalLogo =
    hospitalBrand.smallLogoDataUrl ?? hospitalBrand.logoDataUrl;
  const displayName = session?.user.name ?? "Staff User";
  const patientsSummary = usePatientsSummary();
  const totalPatients = patientsSummary.data?.pagination.total;
  const noticesQuery = useSettingsList(
    "notices",
    1,
    50,
    "",
    "active",
    "createdAt",
    "desc",
  );
  const notices = useMemo(
    () =>
      (noticesQuery.data?.data ?? [])
        .map(noticeFromRecord)
        .filter((notice): notice is NoticeItem => notice !== null),
    [noticesQuery.data?.data],
  );

  const isChildActive = (child: NavChild): boolean =>
    Boolean(
      (child.to &&
        (location.pathname === child.to ||
          (!child.end && location.pathname.startsWith(`${child.to}/`)))) ||
      child.children?.some(isChildActive),
    );
  const isItemActive = (item: NavItem) => {
    if (item.children) return item.children.some(isChildActive);
    if (!item.to) return false;
    return item.end
      ? location.pathname === item.to
      : location.pathname.startsWith(item.to);
  };

  const [rail, setRail] = useState<boolean>(() => {
    const stored = window.localStorage.getItem(SIDEBAR_RAIL_KEY);
    return stored === null ? true : stored === "1";
  });
  const [isWide, setIsWide] = useState<boolean>(
    () => window.matchMedia("(min-width: 961px)").matches,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmation: "",
  });
  const [passwordUpdating, setPasswordUpdating] = useState(false);
  const { showToast } = useToast();
  const [signOutDialog, setSignOutDialog] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profileDialog, setProfileDialog] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [noticeListOpen, setNoticeListOpen] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState<NoticeItem | null>(null);
  const noticeMenuRef = useRef<HTMLDivElement>(null);
  const [patientSearch, setPatientSearch] = useState("");
  const [patientSearchOpen, setPatientSearchOpen] = useState(false);
  const patientSearchRef = useRef<HTMLDivElement>(null);
  const debouncedPatientSearch = usePatientGlobalSearch(patientSearch);
  const patientSearchResults = usePatientGlobalLookup(debouncedPatientSearch);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    const activeGroup = navGroups.find((group) =>
      group.items.some(isItemActive),
    );
    return new Set(
      navGroups
        .filter((group) => group !== activeGroup)
        .map((group) => group.label),
    );
  });
  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(
    () =>
      new Set(
        navGroups.flatMap((group) =>
          group.items
            .filter((item) => item.children && isItemActive(item))
            .map((item) => item.label),
        ),
      ),
  );
  const [railFlyout, setRailFlyout] = useState<{
    label: string;
    top: number;
    children: NavChild[];
    height?: number;
  } | null>(null);
  const railFlyoutRef = useRef<HTMLDivElement>(null);

  // Icon-only rail rendering only applies at desktop widths — below that
  // the sidebar is an on-demand drawer where per-group collapse still applies.
  const iconOnly = rail && isWide;

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_RAIL_KEY, rail ? "1" : "0");
  }, [rail]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const target = e.target as HTMLInputElement;
      if (target?.type === "number") {
        e.preventDefault();
      }
    };
    document.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, []);

  // Inline submenus are hidden in the icon-only rail (no room for labels), so
  // a flyout stands in for them there — close it whenever it stops applying.
  useEffect(() => {
    if (!iconOnly) setRailFlyout(null);
  }, [iconOnly]);
  useEffect(() => setRailFlyout(null), [location.pathname]);
  useEffect(() => {
    if (!railFlyout) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setRailFlyout(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [railFlyout]);

  // The flyout's initial `top` only knows the trigger button's position, not
  // its own (variable, children-count-dependent) height — so long submenus
  // opened near the bottom of the rail could render mostly below the
  // viewport, clipped instead of scrolled. Once mounted we know the real
  // height, so nudge `top` up just enough to keep the whole card on screen.
  useLayoutEffect(() => {
    if (!railFlyout) return;
    const el = railFlyoutRef.current;
    if (!el) return;
    const margin = 12;
    const height = railFlyout.height ?? el.getBoundingClientRect().height;
    const maxTop = Math.max(
      margin,
      window.innerHeight - height - margin,
    );
    const nextTop = Math.max(margin, Math.min(railFlyout.top, maxTop));
    if (railFlyout.height === undefined || nextTop !== railFlyout.top) {
      setRailFlyout((current) =>
        current && current.label === railFlyout.label
          ? { ...current, top: nextTop, height }
          : current,
      );
    }
  }, [railFlyout]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 961px)");
    const onChange = () => setIsWide(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => setDrawerOpen(false), [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node))
        setUserMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [userMenuOpen]);

  useEffect(() => setNoticeListOpen(false), [location.pathname]);

  useEffect(() => {
    if (!noticeListOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!noticeMenuRef.current?.contains(event.target as Node))
        setNoticeListOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNoticeListOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [noticeListOpen]);

  useEffect(() => setPatientSearchOpen(false), [location.pathname]);

  useEffect(() => {
    if (!patientSearchOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!patientSearchRef.current?.contains(event.target as Node))
        setPatientSearchOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPatientSearchOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [patientSearchOpen]);

  const goToPatientProfile = (patientId: string) => {
    setPatientSearch("");
    setPatientSearchOpen(false);
    void navigate(`/patients/${patientId}`);
  };

  const goToBillDetail = (billModule: string, recordId: string) => {
    setPatientSearch("");
    setPatientSearchOpen(false);
    switch (billModule) {
      case "OPD":
        void navigate(`/opd/${recordId}?tab=bill-details`);
        break;
      case "EMG":
        void navigate(`/emg/${recordId}?tab=bill-details`);
        break;
      case "Investigation":
        void navigate(`/investigation-bill/${recordId}`);
        break;
      case "IPD":
        void navigate(`/ipd-daycare/${recordId}?tab=bill-details`);
        break;
      default:
        void navigate(`/patients/${recordId}`);
    }
  };

  const submitPatientSearch = (event: FormEvent) => {
    event.preventDefault();
    if (!canSearchPatientGlobal(patientSearch)) return;
    const normalizedSearch =
      normalizePatientGlobalSearch(patientSearch).toLowerCase();
    const options = patientSearchResults.data?.data ?? [];
    if (isPatientBillSearch(patientSearch)) {
      const exactBill = options.find(
        (patient): patient is PatientUhidOption =>
          patient.matchType === "bill" &&
          patient.code.toLowerCase() === normalizedSearch,
      );
      if (exactBill && exactBill.billModule && exactBill.recordId) {
        goToBillDetail(exactBill.billModule, exactBill.recordId);
        return;
      }
      const firstOption = options[0];
      if (
        options.length === 1 &&
        firstOption &&
        firstOption.matchType === "bill" &&
        firstOption.billModule &&
        firstOption.recordId
      ) {
        goToBillDetail(firstOption.billModule, firstOption.recordId);
        return;
      }
    } else {
      const exactPatient = options.find(
        (patient) => patient.code.toLowerCase() === normalizedSearch,
      );
      if (exactPatient) {
        goToPatientProfile(exactPatient.id);
        return;
      }
      const firstOption = options[0];
      if (options.length === 1 && firstOption) {
        goToPatientProfile(firstOption.id);
        return;
      }
    }
    setPatientSearchOpen(true);
  };

  const filteredGroups = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return navGroups;
    return navGroups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.label.toLowerCase().includes(term) ||
            item.children?.some((child) =>
              child.label.toLowerCase().includes(term),
            ),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [query]);

  const toggleGroup = (label: string) => {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const toggleSubmenu = (label: string) => {
    setOpenSubmenus((current) => {
      const next = new Set(current);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const renderNavChildren = (
    children: NavChild[],
    parentKey: string,
    depth: number,
  ) => (
    <div className={`nav-children${depth > 1 ? " nested" : ""}`}>
      {children.map((child) => {
        const key = `${parentKey}>${child.label}`;
        if (child.children) {
          const isOpen = openSubmenus.has(key);
          const isActive = isChildActive(child);
          return (
            <div className="nav-child-wrap" key={key}>
              <button
                type="button"
                className={`nav-child nav-child-toggle${isOpen ? " open" : ""}${isActive ? " active" : ""}`}
                onClick={() => toggleSubmenu(key)}
                aria-expanded={isOpen}
                title={child.label}
              >
                <span className="nav-child-label">{child.label}</span>
                <IconChevron
                  className={`nav-item-chevron${isOpen ? "" : " collapsed"}`}
                />
              </button>
              {isOpen
                ? renderNavChildren(child.children, key, depth + 1)
                : null}
            </div>
          );
        }
        return child.to ? (
          <NavLink
            key={key}
            to={child.to}
            end={child.end ?? false}
            title={child.label}
            className={({ isActive }) =>
              `nav-child${isActive ? " active" : ""}`
            }
          >
            <span className="nav-child-label">{child.label}</span>
          </NavLink>
        ) : (
          <div
            className="nav-child soon"
            key={key}
            title={child.label}
            aria-disabled="true"
          >
            <span className="nav-child-label">{child.label}</span>
            <span className="soon-pill">
              <IconLock />
              Soon
            </span>
          </div>
        );
      })}
    </div>
  );

  const countFor = (key?: NavItem["countKey"]) => {
    if (key !== "patients") return null;
    if (patientsSummary.isLoading)
      return <span className="sk sk-inline" aria-label="Loading count" />;
    return totalPatients?.toLocaleString() ?? "0";
  };

  const signOut = async () => {
    setSigningOut(true);
    try {
      await endSession();
    } catch {
      /* Always clear the local session. */
    }
    logout();
    void navigate("/login", { replace: true });
  };

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (passwords.newPassword.length < 8) {
      showToast("New password must be at least 8 characters.", "error");
      return;
    }
    if (passwords.newPassword !== passwords.confirmation) {
      showToast("New password and confirmation do not match.", "error");
      return;
    }
    const confirmed = await confirmUpdate("your account password");
    if (!confirmed) return;
    setPasswordUpdating(true);
    try {
      replaceSession(
        await changeOwnPassword({
          currentPassword: passwords.currentPassword,
          newPassword: passwords.newPassword,
          remember: isRememberedSession(),
        }),
      );
      setPasswords({ currentPassword: "", newPassword: "", confirmation: "" });
      setPasswordDialog(false);
      setPasswordUpdating(false);
      showToast("Password changed successfully.", "success");
    } catch {
      setPasswordUpdating(false);
      showToast(
        "The password could not be changed. Check your current password and try again.",
        "error",
      );
    }
  };

  return (
    <div className={`app-shell${rail ? " sidebar-rail" : ""}`}>
      {drawerOpen ? (
        <div
          className="sidebar-overlay"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      ) : null}

      <aside className={`sidebar${drawerOpen ? " open" : ""}`}>
        <div className="brand">
          <span
            className={`brand-mark${hospitalLogo ? " has-logo" : ""}`}
            aria-hidden="true"
          >
            {hospitalLogo ? (
              <img className="brand-logo" src={hospitalLogo} alt="" />
            ) : (
              <IconCross />
            )}
          </span>
          <span className="brand-text">
            <strong>{hospitalTitle}</strong>
            <small>Hospital workspace</small>
          </span>
          <button
            className="sidebar-close"
            type="button"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
          >
            <IconClose />
          </button>
        </div>

        <label className="sidebar-search" aria-label="Search navigation">
          <IconSearch />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search modules…"
          />
        </label>

        <nav className="main-nav" aria-label="Main navigation">
          {filteredGroups.map((group) => {
            const isCollapsed =
              !group.hideHeader &&
              !iconOnly &&
              !query.trim() &&
              collapsedGroups.has(group.label);
            return (
              <div className="nav-group" key={group.label}>
                {group.hideHeader ? null : (
                  <button
                    type="button"
                    className="nav-group-header"
                    onClick={() => toggleGroup(group.label)}
                    aria-expanded={!isCollapsed}
                  >
                    <span>{group.label}</span>
                    <IconChevron
                      className={`nav-group-chevron${isCollapsed ? " collapsed" : ""}`}
                    />
                  </button>
                )}
                {isCollapsed ? null : (
                  <div className="nav-group-items">
                    {group.items.map((item) => {
                      if (item.children) {
                        const isOpen = iconOnly
                          ? railFlyout?.label === item.label
                          : openSubmenus.has(item.label);
                        const isActive = isItemActive(item);
                        return (
                          <div className="nav-item-wrap" key={item.label}>
                            <button
                              type="button"
                              className={`nav-item nav-item-toggle${isOpen ? " open" : ""}${isActive ? " active" : ""}`}
                              onClick={(event) => {
                                if (iconOnly) {
                                  const rect =
                                    event.currentTarget.getBoundingClientRect();
                                  const children = item.children!;
                                  setRailFlyout((current) =>
                                    current?.label === item.label
                                      ? null
                                      : {
                                          label: item.label,
                                          top: rect.top,
                                          children,
                                        },
                                  );
                                } else {
                                  toggleSubmenu(item.label);
                                }
                              }}
                              aria-expanded={isOpen}
                              title={item.label}
                            >
                              <span className="nav-icon" aria-hidden="true">
                                <item.icon />
                              </span>
                              <span className="nav-text">{item.label}</span>
                              <IconChevron
                                className={`nav-item-chevron${isOpen ? "" : " collapsed"}`}
                              />
                            </button>
                            {!iconOnly && isOpen
                              ? renderNavChildren(item.children, item.label, 1)
                              : null}
                          </div>
                        );
                      }
                      return item.to ? (
                        <NavLink
                          key={item.label}
                          to={item.to}
                          end={item.end ?? false}
                          title={item.label}
                          className={({ isActive }) =>
                            `nav-item${isActive ? " active" : ""}`
                          }
                        >
                          <span className="nav-icon" aria-hidden="true">
                            <item.icon />
                          </span>
                          <span className="nav-text">{item.label}</span>
                          {item.countKey ? (
                            <span className="nav-count">
                              {countFor(item.countKey)}
                            </span>
                          ) : null}
                        </NavLink>
                      ) : (
                        <div
                          className="nav-item soon"
                          key={item.label}
                          title={item.label}
                          aria-disabled="true"
                        >
                          <span className="nav-icon" aria-hidden="true">
                            <item.icon />
                          </span>
                          <span className="nav-text">{item.label}</span>
                          <span className="soon-pill">
                            <IconLock />
                            Soon
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {filteredGroups.length === 0 ? (
            <p className="nav-empty">No modules match “{query}”.</p>
          ) : null}
        </nav>
      </aside>

      {railFlyout ? (
        <>
          <div
            className="rail-flyout-backdrop"
            onClick={() => setRailFlyout(null)}
            aria-hidden="true"
          />
          <div
            ref={railFlyoutRef}
            className="rail-flyout"
            style={{
              top: railFlyout.top,
              height: railFlyout.height,
              maxHeight: "calc(100dvh - 24px)",
            }}
            role="menu"
          >
            <div className="rail-flyout-title">{railFlyout.label}</div>
            {renderNavChildren(railFlyout.children, railFlyout.label, 1)}
          </div>
        </>
      ) : null}

      <div className="main-area">
        <header className="topbar">
          <div className="topbar-left">
            <button
              className="icon-button sidebar-rail-toggle"
              type="button"
              onClick={() => setRail((value) => !value)}
              aria-label={rail ? "Expand sidebar" : "Collapse sidebar"}
              title={rail ? "Expand sidebar" : "Collapse sidebar"}
            >
              <IconChevron
                className={rail ? "chevron-right" : "chevron-left"}
              />
            </button>
            <button
              className="hamburger"
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
            >
              <IconMenu />
            </button>
            <div>
              <p className="eyebrow">Care coordination</p>
              <strong>Hospital Operations</strong>
            </div>
          </div>
          <div className="topbar-search" ref={patientSearchRef}>
            <form role="search" onSubmit={submitPatientSearch}>
              <IconSearch />
              <input
                type="search"
                value={patientSearch}
                onChange={(event) => {
                  setPatientSearch(event.target.value);
                  setPatientSearchOpen(true);
                }}
                onFocus={() => setPatientSearchOpen(true)}
                placeholder="Search patient by UHID, name, phone or #bill no"
                aria-label="Search patient by UHID, name, phone or bill number"
              />
            </form>
            {patientSearchOpen && canSearchPatientGlobal(patientSearch) ? (
              <div className="topbar-search-panel" role="listbox">
                {patientSearchResults.isFetching ? (
                  <div
                    className="topbar-search-skeleton"
                    role="status"
                    aria-label="Searching patients"
                  >
                    <span className="sk sk-line" />
                    <span className="sk sk-line" />
                    <span className="sk sk-line short" />
                  </div>
                ) : patientSearchResults.isError ? (
                  <div className="topbar-search-empty">
                    Patient search could not be loaded
                  </div>
                ) : patientSearchResults.data &&
                  patientSearchResults.data.data.length > 0 ? (
                  patientSearchResults.data.data.map((patient) => {
                    const secondary =
                      patient.matchType === "bill"
                        ? patient.uhid
                          ? `UHID ${patient.uhid}`
                          : "UHID -"
                        : patient.phone;
                    const handleClick = () => {
                      if (
                        patient.matchType === "bill" &&
                        patient.billModule &&
                        patient.recordId
                      ) {
                        goToBillDetail(patient.billModule, patient.recordId);
                      } else {
                        goToPatientProfile(patient.id);
                      }
                    };
                    return (
                      <button
                        key={`${patient.matchType ?? "patient"}-${patient.code}-${patient.id}`}
                        type="button"
                        className="topbar-search-option"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={handleClick}
                      >
                        <strong>{patient.code}</strong>
                        <span>{patient.label}</span>
                        {secondary ? (
                          <span className="topbar-search-option-phone">
                            {secondary}
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                ) : (
                  <div className="topbar-search-empty">No patients found</div>
                )}
              </div>
            ) : null}
          </div>
          <div className="topbar-right">
            <button
              className="icon-button"
              type="button"
              onClick={toggleTheme}
              aria-label={
                theme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
              title={
                theme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
            >
              {theme === "dark" ? <IconSun /> : <IconMoon />}
            </button>
            <div className="notice-menu-wrap" ref={noticeMenuRef}>
              <button
                className={`icon-button notice-button${notices.length > 0 ? " has-notices" : ""}`}
                type="button"
                aria-label={`Notices${notices.length > 0 ? ` (${notices.length})` : ""}`}
                aria-haspopup="dialog"
                aria-expanded={noticeListOpen}
                aria-controls="notice-list-panel"
                title="Notices"
                onClick={() => {
                  setUserMenuOpen(false);
                  setPatientSearchOpen(false);
                  setNoticeListOpen((open) => {
                    if (!open) void noticesQuery.refetch();
                    return !open;
                  });
                }}
              >
                <IconBell />
                {notices.length > 0 ? (
                  <span className="notice-count" aria-hidden="true">
                    {notices.length > 99 ? "99+" : notices.length}
                  </span>
                ) : null}
              </button>
              {noticeListOpen ? (
                <div
                  className="notice-panel"
                  id="notice-list-panel"
                  role="dialog"
                  aria-label="Hospital notices"
                >
                  <div className="notice-panel-header">
                    <div>
                      <strong>Notices</strong>
                      <small>
                        {notices.length} active{" "}
                        {notices.length === 1 ? "notice" : "notices"}
                      </small>
                    </div>
                    <button
                      type="button"
                      onClick={() => void noticesQuery.refetch()}
                      disabled={noticesQuery.isFetching}
                    >
                      {noticesQuery.isFetching ? "Refreshing..." : "Refresh"}
                    </button>
                  </div>
                  <div className="notice-list">
                    {noticesQuery.isLoading ? (
                      <div
                        className="notice-list-loading"
                        role="status"
                        aria-label="Loading notices"
                      >
                        <span className="sk sk-line" />
                        <span className="sk sk-line" />
                        <span className="sk sk-line short" />
                      </div>
                    ) : noticesQuery.isError ? (
                      <div className="notice-list-message">
                        <strong>Unable to load notices</strong>
                        <span>Please try refreshing the list.</span>
                      </div>
                    ) : notices.length === 0 ? (
                      <div className="notice-list-message">
                        <strong>No active notices</strong>
                        <span>New hospital notices will appear here.</span>
                      </div>
                    ) : (
                      notices.map((notice) => (
                        <button
                          className="notice-list-item"
                          type="button"
                          key={notice.id}
                          aria-label={notice.notice || "Open notice image"}
                          onClick={() => {
                            setNoticeListOpen(false);
                            setSelectedNotice(notice);
                          }}
                        >
                          <span
                            className={`notice-priority-dot ${notice.priorityLevel}`}
                            aria-hidden="true"
                          />
                          <span className="notice-list-copy">
                            <span className="notice-list-meta">
                              <span
                                className={`notice-priority-badge ${notice.priorityLevel}`}
                              >
                                {notice.priorityLevel}
                              </span>
                              <time>
                                {notice.createdAt
                                  ? formatDateTime(notice.createdAt)
                                  : "Date unavailable"}
                              </time>
                            </span>
                            <span className="notice-list-content">
                              {notice.images[0] ? (
                                <img
                                  className="notice-list-thumbnail"
                                  src={notice.images[0].src}
                                  alt=""
                                />
                              ) : null}
                              {notice.notice ? (
                                <span className="notice-list-preview">
                                  {notice.notice}
                                </span>
                              ) : null}
                            </span>
                          </span>
                          <span
                            className="notice-list-arrow"
                            aria-hidden="true"
                          >
                            ›
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ) : null}
            </div>
            {/* <NavLink className="icon-button" to="/settings" aria-label="Settings" title="Settings"><IconSettings /></NavLink> */}
            <div className="user-menu-wrap" ref={userMenuRef}>
              <button
                className="user-chip"
                type="button"
                onClick={() => setUserMenuOpen((value) => !value)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label="Account menu"
              >
                <span>{initialsFor(displayName)}</span>
                <div>
                  <strong>{displayName}</strong>
                  <small>{session?.user.role ?? "Staff"}</small>
                </div>
                <IconChevron
                  className={`user-chip-chevron${userMenuOpen ? " open" : ""}`}
                />
              </button>
              {userMenuOpen ? (
                <div className="user-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setProfileDialog(true);
                    }}
                  >
                    <IconIdCard />
                    Profile
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="danger"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setSignOutDialog(true);
                    }}
                  >
                    <IconLogout />
                    Logout
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>
        {session?.user.mustChangePassword ? (
          <div className="password-required-banner">
            <span>Your account is using a temporary password.</span>
            <button type="button" onClick={() => setPasswordDialog(true)}>
              Change it now
            </button>
          </div>
        ) : null}
        <main className="page-container">
          <div className="page-transition" key={location.pathname}>
            <Outlet />
          </div>
        </main>
      </div>
      <Modal
        open={selectedNotice !== null}
        onClose={() => setSelectedNotice(null)}
        labelledBy="notice-detail-title"
        className="notice-detail-modal"
      >
        <header>
          <div>
            <p className="eyebrow">Hospital notice</p>
            <h2 id="notice-detail-title">Notice details</h2>
          </div>
          <button
            type="button"
            onClick={() => setSelectedNotice(null)}
            aria-label="Close notice"
          >
            <IconClose />
          </button>
        </header>
        {selectedNotice ? (
          <article className="notice-detail-body">
            <div className="notice-detail-meta">
              <span
                className={`notice-priority-badge ${selectedNotice.priorityLevel}`}
              >
                {selectedNotice.priorityLevel} priority
              </span>
              <time>
                {selectedNotice.createdAt
                  ? `Published ${formatDateTime(selectedNotice.createdAt)}`
                  : "Publication date unavailable"}
              </time>
            </div>
            {selectedNotice.notice ? <p>{selectedNotice.notice}</p> : null}
            {selectedNotice.images.length > 0 ? (
              <div className="notice-detail-images">
                {selectedNotice.images.map((image) => (
                  <img key={image.src} src={image.src} alt={image.alt} />
                ))}
              </div>
            ) : null}
            {selectedNotice.updatedAt &&
            selectedNotice.updatedAt !== selectedNotice.createdAt ? (
              <small>
                Last updated {formatDateTime(selectedNotice.updatedAt)}
              </small>
            ) : null}
          </article>
        ) : null}
        <footer className="notice-detail-footer">
          <button
            className="button button-primary"
            type="button"
            onClick={() => setSelectedNotice(null)}
          >
            Close
          </button>
        </footer>
      </Modal>
      <Modal
        open={passwordDialog}
        onClose={() => setPasswordDialog(false)}
        closeOnBackdrop={false}
        labelledBy="staff-password-title"
        className="account-password-modal"
      >
        <header>
          <div>
            <p className="eyebrow">Account security</p>
            <h2 id="staff-password-title">Change password</h2>
          </div>
          <button
            type="button"
            onClick={() => setPasswordDialog(false)}
            disabled={passwordUpdating}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <form onSubmit={(event) => void submitPassword(event)}>
          <label>
            Current password
            <input
              type="password"
              value={passwords.currentPassword}
              onChange={(event) =>
                setPasswords((current) => ({
                  ...current,
                  currentPassword: event.target.value,
                }))
              }
              autoComplete="current-password"
              required
            />
          </label>
          <label>
            New password
            <input
              type="password"
              minLength={8}
              maxLength={72}
              value={passwords.newPassword}
              onChange={(event) =>
                setPasswords((current) => ({
                  ...current,
                  newPassword: event.target.value,
                }))
              }
              autoComplete="new-password"
              required
            />
          </label>
          <label>
            Confirm new password
            <input
              type="password"
              minLength={8}
              maxLength={72}
              value={passwords.confirmation}
              onChange={(event) =>
                setPasswords((current) => ({
                  ...current,
                  confirmation: event.target.value,
                }))
              }
              autoComplete="new-password"
              required
            />
          </label>
          <div className="bed-modal-actions">
            <button
              className="button"
              type="button"
              onClick={() => setPasswordDialog(false)}
              disabled={passwordUpdating}
            >
              Cancel
            </button>
            <button
              className="button button-primary"
              type="submit"
              disabled={passwordUpdating}
            >
              {passwordUpdating ? "Changing..." : "Change password"}
            </button>
          </div>
        </form>
      </Modal>
      <Modal
        open={profileDialog}
        onClose={() => setProfileDialog(false)}
        labelledBy="staff-profile-title"
        className="account-profile-modal"
      >
        <header>
          <div>
            <p className="eyebrow">Account</p>
            <h2 id="staff-profile-title">My profile</h2>
          </div>
          <button
            type="button"
            onClick={() => setProfileDialog(false)}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="profile-detail-grid">
          <div>
            <span>Name</span>
            <strong>{displayName}</strong>
          </div>
          <div>
            <span>Role</span>
            <strong>{session?.user.role ?? "Staff"}</strong>
          </div>
          <div>
            <span>Login ID</span>
            <strong>{session?.user.loginId ?? "—"}</strong>
          </div>
          <div>
            <span>Email</span>
            <strong>{session?.user.email ?? "—"}</strong>
          </div>
        </div>
        <div className="bed-modal-actions">
          <button
            className="button button-secondary"
            type="button"
            onClick={() => {
              setProfileDialog(false);
              setPasswordDialog(true);
            }}
          >
            Change password
          </button>
          <button
            className="button button-primary"
            type="button"
            onClick={() => setProfileDialog(false)}
          >
            Close
          </button>
        </div>
      </Modal>
      <ConfirmDialog
        open={signOutDialog}
        title="Sign out of your account?"
        description="You'll need to sign in again to access patient records and hospital operations."
        confirmLabel="Sign out"
        pendingLabel="Signing out..."
        tone="danger"
        pending={signingOut}
        onCancel={() => setSignOutDialog(false)}
        onConfirm={() => void signOut()}
      />
    </div>
  );
};
